package fr.arcplex.network.impl.legacy

import fr.arcplex.network.AuthProvider
import fr.arcplex.network.RendererKind
import fr.arcplex.network.SoonerApi
import fr.arcplex.network.logging.Logger
import fr.arcplex.network.model.Branding
import fr.arcplex.network.model.Film
import fr.arcplex.network.model.PlaybackSource
import fr.arcplex.network.model.RailItem
import fr.arcplex.network.model.Screen
import fr.arcplex.network.model.ScreenAction
import fr.arcplex.network.model.ScreenKind
import fr.arcplex.network.model.SearchPage
import kotlinx.coroutines.CancellationException

/**
 * `SoonerApi` implementation for the legacy "generique" (digital-backstage) API.
 *
 * This is the only place that knows the legacy endpoints, JSON shapes, action
 * URLs and the boot token dance. The UI only sees [Screen], [Film] and
 * [PlaybackSource].
 */
class LegacySoonerApi(
    baseUrl: String,
    private val auth: AuthProvider,
    allowInvalidCertificates: Boolean = false
) : SoonerApi {

    private val client = DefaultApi(baseUrl, auth, allowInvalidCertificates)
    private val mapper = LegacyScreenMapper(client, auth)

    override val renderer: RendererKind = RendererKind.LEGACY

    override suspend fun cachedBranding(): Branding = Branding()

    override suspend fun refreshBranding(): Result<Branding> = Result.success(Branding())

    override suspend fun fetchRoot(): Result<Screen> = apiCall("root") {
        auth.saveUserToken(BOOT_USER_TOKEN)

        var response = client.getGeneriqueGetRootPage("", "", "", "5", auth.getUserToken() ?: "")
        var screen: Screen? = null

        while (response != null) {
            val action = response.getString("action")
            if (action != "callURL" && action != "getToken") break

            val url = response.getString("url") ?: ""
            val prettyUrl = response.getString("pretty_url") ?: ""
            Logger.d("FILMO API", "action: $action -> $url")

            response = client.getGenericCallURL(prettyUrl, url, auth.getUserToken() ?: "")

            when (response?.getString("action")) {
                null -> break
                "getToken", "recallLast" -> {
                    val pretty = response.getString("pretty_url") ?: ""
                    response = client.getGeneriqueGetToken(pretty, "", "", "5", auth.getUserToken() ?: "")
                    auth.saveUserToken(BOOT_USER_TOKEN)
                }
                "refreshPage" -> {
                    val pageId = response.getString("idpage") ?: ""
                    val page = response.get("content")?.get("page")
                    if (page != null) screen = mapper.parse(pageId, page)
                }
            }
        }

        // A chain can also end on the page body itself.
        if (screen == null) {
            val page = response?.get("content")?.get("page")
            if (page != null) screen = mapper.parse(response.getString("idpage"), page)
        }

        screen ?: error("no page in root response")
    }

    override suspend fun fetchScreen(actionUrl: String): Result<Screen> = apiCall("screen") {
        val response = client.getGenericCallURL("", actionUrl, auth.getUserToken() ?: "")
            ?: error("empty response for $actionUrl")
        val page = response.get("content")?.get("page")
            ?: error("no page in response (action=${response.getString("action")})")
        mapper.parse(
            id = response.getString("idpage") ?: page.getString("url") ?: actionUrl,
            json = page
        )
    }

    override suspend fun fetchFilm(filmId: String): Result<Film> = apiCall("film $filmId") {
        val response = client.getGeneriqueGetPage(
            prettyUrl = "",
            theOrigin = "",
            prefix = "",
            version = "5",
            userToken = auth.getUserToken() ?: "",
            id_film = filmId
        ) ?: error("empty response for film $filmId")
        val page = response.get("content")?.get("page")
            ?: error("no page in film response")
        Film(id = filmId, screen = mapper.parse(filmId, page, ScreenKind.FILM))
    }

    override suspend fun search(
        actionUrl: String,
        query: String,
        validate: Boolean
    ): Result<SearchPage> = apiCall("search") {
        val url = buildSearchUrl(actionUrl, query, validate)
        Logger.d("SEARCH", "query='$query' validate=$validate url=$url")

        val response = client.getGenericCallURL("", url, auth.getUserToken() ?: "")
            ?: return@apiCall SearchPage(null, emptyList())

        val result = parseSearchResponse(response, query)
        Logger.d(
            "SEARCH",
            "applied=${result != null} action=${response.getString("action")} " +
                "target=${response.getString("target_to_refresh")} " +
                "str=${response.getString("str_recherche")}"
        )
        result ?: SearchPage(null, emptyList())
    }

    override suspend fun resolvePlayback(item: RailItem): Result<PlaybackSource> =
        apiCall("playback") {
            val actionUrl = item.action?.url?.trim().orEmpty()
            require(actionUrl.isNotEmpty()) { "no action url for playback" }

            val response = client.getGenericCallURL("", actionUrl, auth.getUserToken() ?: "")
                ?: error("empty playback response")

            val payload = mutableMapOf<String, String>()
            response.getString("id_programme")?.let { payload["id_programme"] = it }
            response.getString("id_client")?.let { payload["id_client"] = it }
            response.get("player_params")?.getString("type")?.let { payload["type"] = it }
            payload["user_token"] = auth.getUserToken() ?: ""
            Logger.d("payload", payload.toString())

            val licenceUrl = response.getString("url_licence")?.substringBefore('?') ?: ""
            val dashUrl = response.get("possible_videos")?.getString("DASH")
                ?: error("no DASH stream in playback response")

            // Vualto token for the cold-start cert leg (CAQ -> vudrm proxy carries it
            // as X-VUDRM-TOKEN). viewVideo does not expose one, but
            // getInfosViewVideoWebV2 mints it per playback with the same recipe
            // as the website player. Failure here is non-fatal: playback proceeds
            // without a token and the player logs hasToken=false.
            val vudrmToken = response.getString("vudrm_token") ?: run {
                val idProg = payload["id_programme"]
                if (idProg.isNullOrEmpty()) {
                    null
                } else {
                    try {
                        client.getGenericCallURL(
                            "",
                            "/api/generique/V5/getInfosViewVideoWebV2?idfilm=$idProg&type=FILM",
                            auth.getUserToken() ?: ""
                        )?.get("details")?.getString("token")
                    } catch (_: Exception) {
                        Logger.d("drm", "infos token fetch failed, continuing without vudrm token")
                        null
                    }
                }
            }

            PlaybackSource(
                url = dashUrl,
                licenceUrl = licenceUrl,
                payload = payload,
                vudrmToken = vudrmToken
            )
        }

    override fun resolveAction(item: RailItem, currentScreen: Screen?): ScreenAction {
        val url = item.action?.url?.trim().orEmpty()
        val actionType = item.action?.type
        Logger.d("ACTION", "item='${item.label ?: item.content}' type=$actionType url=$url")

        if (actionType == "displayMenu") return ScreenAction.ToggleMenu

        // A search result is a film poster: btn_OK carries the trailer playback
        // (and resolvePlayback cannot resolve its nested DASH source), but
        // selecting the result must open the film screen.
        if (currentScreen?.kind == ScreenKind.SEARCH) {
            val filmId = item.programId?.takeIf { it.isNotBlank() }
                ?: url.substringAfter("idprog=", "").substringBefore('&').takeIf { it.isNotEmpty() }
            if (filmId != null) return ScreenAction.OpenFilm(filmId)
        }

        if (url.isEmpty()) {
            Logger.d("ACTION", "no action url, ignoring")
            return ScreenAction.Ignore
        }

        return when {
            url.contains("idfilm=", ignoreCase = true) -> {
                val id = url.substringAfter("idfilm=").substringBefore("&")
                if (id.isNotEmpty()) ScreenAction.OpenFilm(id) else ScreenAction.Ignore
            }

            url.contains("viewVideo", ignoreCase = true) ||
                url.contains("playVideo", ignoreCase = true) -> ScreenAction.Play(item)

            url.contains("getPage", ignoreCase = true) ||
                actionType?.let { it in PAGE_ACTIONS } == true -> ScreenAction.OpenScreen(url)

            else -> {
                Logger.e("ACTION", "unhandled action type=$actionType url=$url")
                ScreenAction.Ignore
            }
        }
    }

    private suspend fun <T> apiCall(what: String, block: suspend () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        Logger.e("API", "$what failed: ${e.message}")
        Result.failure(e)
    }

    private companion object {
        /** The box expects this value to bootstrap the session (as before). */
        const val BOOT_USER_TOKEN = "1f170828-02f1-608c-89e6-b91c9ec67f9c"
        val PAGE_ACTIONS = setOf("callURL", "appelWS", "getPage")
    }
}
